import { useState } from 'react'
import useNetwork from '../models/useNetwork'
import useNode, { isNodeOffline } from '../models/useNode'
import useMergedData from '../hooks/useMergedData'
import Typography from '@mui/material/Typography'
import IconNode from './IconNode'
import NetworkInsert from './NetworkInsert'
import CreateRefusal from './CreateRefusal'
import List from '@mui/material/List'
import ListItemLink from './ListItemLink'
import NetworksEmptyState from './documentation/NetworksEmptyState'
import { useOrgRole } from '../hooks/useOrgRole'
import { limitRefusal, type LimitRefusal } from '../utilities/limitRefusal'
import { countByNetwork, countLabel } from '../utilities/nodeStatus'

export default function Networks() {
  const { networks, add } = useNetwork()
  const [refusal, setRefusal] = useState<Pick<LimitRefusal, 'message' | 'upgradeUrl'> | null>(null)
  const { can } = useOrgRole()
  // ⚠ Any member creates a network and becomes its owner (docs/PERMISSIONS.md § 5 Network, decided
  // Q-3, app#376). This was Admin only here and in the side menu, and ungated on the other three
  // create paths (the network dialog, onboarding, /register), so the five disagreed.
  const canCreate = can('network.create')
  // The same node source as the Nodes tab, so the two tabs cannot disagree about a count.
  const { nodes } = useMergedData()
  // ⚠ Completeness comes from the STORE, not from `hideOffline`. The button flips `hideOffline`
  // before its refetch returns, so reading the toggle would state "No nodes" for a network whose
  // nodes are all long offline, for the width of that round trip.
  const nodesComplete = useNode(state => state.nodesComplete)
  // ⚠ Not memoized: `useMergedData` returns a fresh `nodes` array on every render, so a
  // `useMemo` keyed on it never hits and only reads as caching that does not happen.
  const counts = countByNetwork(nodes, isNodeOffline)

  // ⚠ `add` throws since app#296 — it used to alert() and swallow — so every one of its three
  // callers must answer for the refusal. Leaving this one bare would turn a spent allowance into
  // an unhandled rejection: a Submit that does nothing at all, which is worse than the alert it
  // replaced.
  const create = async (name: string) => {
    setRefusal(null)
    try {
      await add({ name })
    } catch (e) {
      console.error('network_create failed', e)
      setRefusal(limitRefusal(e) ?? { message: e instanceof Error ? e.message : String(e) })
    }
  }

  if (!networks.length) {
    return <NetworksEmptyState />
  }

  return (
    <>
      <List sx={{ marginBottom: 3 }}>
        {networks.map(({ name, id }) => (
          <ListItemLink key={id || 'new'} to={`/networks/${id}`} icon={<IconNode type="network" size={20} />}>
            <Typography variant="body2">{name}</Typography>
            {/* ⚠ Offline nodes can be hidden, and then the total is not known: see `countLabel`. */}
            <Typography variant="caption" color="textSecondary" component="div">
              {countLabel(id ? counts.get(id) : undefined, nodesComplete)}
            </Typography>
          </ListItemLink>
        ))}
      </List>
      <CreateRefusal message={refusal?.message} upgradeUrl={refusal?.upgradeUrl} />
      <NetworkInsert disabled={!canCreate} onCreate={create} />
    </>
  )
}
